import { cn } from "@repo/design-system/utils";
import type { ComponentPropsWithoutRef } from "react";

/**
 * The `wide` ceiling in px, for layout maths that must know where the centred
 * column ends (e.g. the cart reserving room for the floating buttons). Tailwind
 * needs the class literal below, so a test pins the two together.
 */
export const CONTAINER_WIDE_MAX_PX = 1400;

/**
 * The max-width ceilings a centered page column can pick from. Curated on
 * purpose: pages across the app had each hard-coded their own (`max-w-7xl`,
 * `max-w-[1400px]`, `max-w-[900px]`, ...), which is exactly the drift this
 * component exists to end. Need a width that isn't here? Add it here, don't
 * re-introduce a one-off.
 */
export const MAX_WIDTHS = {
  sm: "max-w-2xl",
  md: "max-w-4xl",
  lg: "max-w-6xl",
  xl: "max-w-7xl",
  wide: "max-w-[1400px]"
} as const;

export type ContainerSize = keyof typeof MAX_WIDTHS;

export interface ContainerProps extends ComponentPropsWithoutRef<"div"> {
  /** Max-width ceiling for the centered column. Defaults to `"xl"`. */
  size?: ContainerSize;
}

/**
 * A centered, max-width page column - the single shared wrapper for page
 * content, replacing the per-page copies.
 *
 * It owns horizontal layout only: centering, the width ceiling, and a `px-6`
 * page gutter (the storefront's baseline). Vertical rhythm (`py-*`,
 * `space-y-*`) is the page's business, passed via `className`, because it
 * varies far more than the horizontal frame does. Want per-breakpoint gutters
 * on a given page? Override via `className` (e.g. `px-4 sm:px-6 lg:px-8`) - but
 * keep it aligned with whatever sits beside it (a full-bleed hero, an aside).
 */
export function Container({
  size = "xl",
  className,
  ...props
}: ContainerProps) {
  return (
    <div
      className={cn("mx-auto w-full px-6", MAX_WIDTHS[size], className)}
      {...props}
    />
  );
}
